"use client";

import React, { useState, useEffect } from "react";
import { X, Check } from "lucide-react";
import { cn } from "@/utils/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import {
  AudienceFilter,
  FilterOperator,
  FilterValue,
  OPERATOR_LABELS,
} from "../../types/audienceTypes";
import { getFieldById, getOperatorsForFieldType } from "../../config/filterFields";

interface FilterValueEditorProps {
  filter: AudienceFilter;
  onUpdate: (updates: Partial<AudienceFilter>) => void;
  onRemove: () => void;
  children: React.ReactNode;
}

export function FilterValueEditor({
  filter,
  onUpdate,
  onRemove,
  children,
}: FilterValueEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localOperator, setLocalOperator] = useState<FilterOperator>(filter.operator);
  const [localValue, setLocalValue] = useState<FilterValue>(filter.value);

  const field = getFieldById(filter.fieldId);

  // Update local state when filter changes
  useEffect(() => {
    setLocalOperator(filter.operator);
    setLocalValue(filter.value);
  }, [filter.operator, filter.value]);

  if (!field) return <>{children}</>;

  const operators = getOperatorsForFieldType(field.type);

  // Handle saving
  const handleSave = () => {
    onUpdate({
      operator: localOperator,
      value: localValue,
    });
    setIsOpen(false);
  };

  // Handle multi-select toggle
  const handleMultiSelectToggle = (optionId: string | number) => {
    const currentValues: (string | number)[] = Array.isArray(localValue)
      ? (localValue as (string | number)[])
      : [];
    const stringId = String(optionId);

    if (currentValues.map(String).includes(stringId)) {
      setLocalValue(currentValues.filter((v: string | number) => String(v) !== stringId) as string[]);
    } else {
      setLocalValue([...currentValues, stringId] as string[]);
    }
  };

  // Render value input based on field type
  const renderValueInput = () => {
    // Skip value input for "is_known" and "is_unknown" operators
    if (localOperator === "is_known" || localOperator === "is_unknown") {
      return (
        <p className="text-sm text-gray-500 italic py-2">
          No value needed for this operator
        </p>
      );
    }

    switch (field.type) {
      case "text":
        return (
          <Input
            className="h-9"
            onChange={(e) => setLocalValue(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            value={(localValue as string) || ""}
          />
        );

      case "number":
        if (localOperator === "between") {
          const rangeValue = localValue as { from: number; to: number } | null;
          return (
            <div className="flex items-center gap-2">
              <Input
                className="h-9 w-24"
                onChange={(e) =>
                  setLocalValue({
                    from: Number(e.target.value),
                    to: rangeValue?.to || 0,
                  })
                }
                placeholder="From"
                type="number"
                value={rangeValue?.from || ""}
              />
              <span className="text-gray-500">to</span>
              <Input
                className="h-9 w-24"
                onChange={(e) =>
                  setLocalValue({
                    from: rangeValue?.from || 0,
                    to: Number(e.target.value),
                  })
                }
                placeholder="To"
                type="number"
                value={rangeValue?.to || ""}
              />
            </div>
          );
        }
        return (
          <Input
            className="h-9 w-32"
            onChange={(e) => setLocalValue(Number(e.target.value))}
            placeholder="Enter value..."
            type="number"
            value={(localValue as number) || ""}
          />
        );

      case "date":
        if (localOperator === "is_between" || localOperator === "is_more_than_days_ago" || localOperator === "is_less_than_days_ago") {
          if (localOperator === "is_more_than_days_ago" || localOperator === "is_less_than_days_ago") {
            return (
              <div className="flex items-center gap-2">
                <Input
                  className="h-9 w-20"
                  onChange={(e) => setLocalValue(Number(e.target.value))}
                  placeholder="Days"
                  type="number"
                  value={(localValue as number) || ""}
                />
                <span className="text-gray-500 text-sm">days ago</span>
              </div>
            );
          }
          const rangeValue = localValue as { from: string; to: string } | null;
          return (
            <div className="flex items-center gap-2">
              <Input
                className="h-9"
                onChange={(e) =>
                  setLocalValue({
                    from: e.target.value,
                    to: rangeValue?.to || "",
                  })
                }
                type="date"
                value={rangeValue?.from || ""}
              />
              <span className="text-gray-500">to</span>
              <Input
                className="h-9"
                onChange={(e) =>
                  setLocalValue({
                    from: rangeValue?.from || "",
                    to: e.target.value,
                  })
                }
                type="date"
                value={rangeValue?.to || ""}
              />
            </div>
          );
        }
        return (
          <Input
            className="h-9"
            onChange={(e) => setLocalValue(e.target.value)}
            type="date"
            value={(localValue as string) || ""}
          />
        );

      case "select":
        return (
          <Select
            onValueChange={(val) => setLocalValue(val)}
            value={(localValue as string) || ""}
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.id} value={String(option.id)}>
                  {option.icon ? `${option.icon} ${option.label}` : option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case "multi_select":
        const selectedValues = Array.isArray(localValue) ? localValue.map(String) : [];
        return (
          <div className="space-y-2">
            <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
              {field.options?.map((option) => {
                const isSelected = selectedValues.includes(String(option.id));
                return (
                  <button
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 transition-colors",
                      isSelected && "bg-orange-50"
                    )}
                    key={option.id}
                    onClick={() => handleMultiSelectToggle(option.id)}
                    type="button"
                  >
                    <span className="flex items-center gap-2">
                      {option.icon && <span>{option.icon}</span>}
                      {option.label}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-orange-600" />}
                  </button>
                );
              })}
            </div>
            {selectedValues.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {selectedValues.map((val) => {
                  const option = field.options?.find((o) => String(o.id) === val);
                  return (
                    <Badge
                      className="bg-orange-100 text-orange-700 border-orange-200"
                      key={val}
                      variant="outline"
                    >
                      {option?.label || val}
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>
        );

      case "boolean":
        return (
          <div className="flex gap-2">
            <Button
              className={cn(
                "flex-1",
                localValue === true && "bg-green-100 text-green-700 border-green-300"
              )}
              onClick={() => setLocalValue(true)}
              size="sm"
              type="button"
              variant="outline"
            >
              Yes
            </Button>
            <Button
              className={cn(
                "flex-1",
                localValue === false && "bg-red-100 text-red-700 border-red-300"
              )}
              onClick={() => setLocalValue(false)}
              size="sm"
              type="button"
              variant="outline"
            >
              No
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0" side="bottom">
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">{field.label}</h4>
            <button
              className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-red-500 transition-colors"
              onClick={() => {
                onRemove();
                setIsOpen(false);
              }}
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {field.description && (
            <p className="text-xs text-gray-500 mt-1">{field.description}</p>
          )}
        </div>

        <div className="p-4 space-y-4">
          {/* Operator Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Condition</label>
            <Select
              onValueChange={(val) => setLocalOperator(val as FilterOperator)}
              value={localOperator}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {operators.map((op) => (
                  <SelectItem key={op} value={op}>
                    {OPERATOR_LABELS[op] || op}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Value</label>
            {renderValueInput()}
          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t bg-gray-50 flex justify-end gap-2">
          <Button
            onClick={() => setIsOpen(false)}
            size="sm"
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleSave}
            size="sm"
            type="button"
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default FilterValueEditor;
